import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, Trash2 } from 'lucide-react';
import { Button, Input, TextArea } from '../shared';

const CreateSubscriptionModal = ({
  isOpen,
  onClose,
  onSave,
  subscription = null,
  isEdit = false,
  isLoading = false
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    features_list: [''],
    price: '',
    currency: 'USD',
    duration_days: 30,
    max_bookings_per_month: '',
    max_services: '',
    max_portfolio_items: '',
    feature_analytics: false,
    feature_custom_branding: false,
    feature_priority_support: false,
    feature_api_access: false,
    feature_advanced_booking: false,
    feature_multi_location: false,
    is_active: true,
    sort_order: 0
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset local loading state when external loading state changes
  useEffect(() => {
    if (!isLoading) {
      setIsSubmitting(false);
    }
  }, [isLoading]);

  useEffect(() => {
    if (isEdit && subscription) {
      setFormData({
        name: subscription.name || '',
        description: subscription.description || '',
        features_list: subscription.features_list && subscription.features_list.length > 0 
          ? subscription.features_list 
          : [''],
        price: subscription.price || '',
        currency: subscription.currency || 'USD',
        duration_days: subscription.duration_days || 30,
        max_bookings_per_month: subscription.max_bookings_per_month || '',
        max_services: subscription.max_services || '',
        max_portfolio_items: subscription.max_portfolio_items || '',
        feature_analytics: subscription.feature_analytics || false,
        feature_custom_branding: subscription.feature_custom_branding || false,
        feature_priority_support: subscription.feature_priority_support || false,
        feature_api_access: subscription.feature_api_access || false,
        feature_advanced_booking: subscription.feature_advanced_booking || false,
        feature_multi_location: subscription.feature_multi_location || false,
        is_active: subscription.is_active !== undefined ? subscription.is_active : true,
        sort_order: subscription.sort_order || 0
      });
    }
  }, [isEdit, subscription]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleFeatureChange = (index, value) => {
    const newFeatures = [...formData.features_list];
    newFeatures[index] = value;
    setFormData(prev => ({
      ...prev,
      features_list: newFeatures
    }));
  };

  const addFeature = () => {
    setFormData(prev => ({
      ...prev,
      features_list: [...prev.features_list, '']
    }));
  };

  const removeFeature = (index) => {
    if (formData.features_list.length > 1) {
      const newFeatures = formData.features_list.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        features_list: newFeatures
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (isSubmitting || isLoading) {
      return;
    }
    
    setIsSubmitting(true);
    setErrors({});
    
    try {
      // Validation
      const newErrors = {};
      if (!formData.name.trim()) newErrors.name = 'Name is required';
      const priceNum = parseFloat(formData.price);
      if (formData.price === '' || formData.price === null || formData.price === undefined || isNaN(priceNum) || priceNum < 0) {
        newErrors.price = 'Price is required and must be 0 or greater';
      }
      if (!formData.duration_days || formData.duration_days <= 0) newErrors.duration_days = 'Duration must be greater than 0';
      
      // Filter out empty features
      const filteredFeatures = formData.features_list.filter(feature => feature.trim() !== '');
      if (filteredFeatures.length === 0) {
        newErrors.features_list = 'At least one feature is required';
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
      }

      // Prepare data for submission
      const submitData = {
        ...formData,
        price: parseFloat(formData.price),
        duration_days: parseInt(formData.duration_days),
        max_bookings_per_month: formData.max_bookings_per_month ? parseInt(formData.max_bookings_per_month) : null,
        max_services: formData.max_services ? parseInt(formData.max_services) : null,
        max_portfolio_items: formData.max_portfolio_items ? parseInt(formData.max_portfolio_items) : null,
        features_list: filteredFeatures,
        sort_order: parseInt(formData.sort_order)
      };

      await onSave(submitData);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      features_list: [''],
      price: '',
      currency: 'USD',
      duration_days: 30,
      max_bookings_per_month: '',
      max_services: '',
      max_portfolio_items: '',
      feature_analytics: false,
      feature_custom_branding: false,
      feature_priority_support: false,
      feature_api_access: false,
      feature_advanced_booking: false,
      feature_multi_location: false,
      is_active: true,
      sort_order: 0
    });
    setErrors({});
    setIsSubmitting(false);
    onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] overflow-y-auto relative my-4">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {isEdit ? 'Edit Subscription' : 'Create New Subscription'}
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>
              
              <div>
                <Input
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  error={errors.name}
                  placeholder="e.g., Basic, Professional, Enterprise"
                  required
                />
              </div>

              <div>
                <TextArea
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Brief description of the subscription"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Input
                    label="Price"
                    name="price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price}
                    onChange={handleInputChange}
                    error={errors.price}
                    placeholder="0.00"
                    required
                  />
                </div>
                <div>
                  <Input
                    label="Currency"
                    name="currency"
                    value={formData.currency}
                    onChange={handleInputChange}
                    placeholder="USD"
                    required
                  />
                </div>
              </div>

              <div>
                <Input
                  label="Duration (days)"
                  name="duration_days"
                  type="number"
                  min="1"
                  value={formData.duration_days}
                  onChange={handleInputChange}
                  error={errors.duration_days}
                  placeholder="30"
                  required
                />
              </div>
            </div>

            {/* Limits */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Limits</h3>
              
              <div>
                <Input
                  label="Max Bookings per Month"
                  name="max_bookings_per_month"
                  type="number"
                  min="0"
                  value={formData.max_bookings_per_month}
                  onChange={handleInputChange}
                  placeholder="Leave empty for unlimited"
                />
              </div>

              <div>
                <Input
                  label="Max Services"
                  name="max_services"
                  type="number"
                  min="0"
                  value={formData.max_services}
                  onChange={handleInputChange}
                  placeholder="Leave empty for unlimited"
                />
              </div>

              <div>
                <Input
                  label="Max Portfolio Items"
                  name="max_portfolio_items"
                  type="number"
                  min="0"
                  value={formData.max_portfolio_items}
                  onChange={handleInputChange}
                  placeholder="Leave empty for unlimited"
                />
              </div>

              <div>
                <Input
                  label="Sort Order"
                  name="sort_order"
                  type="number"
                  min="0"
                  value={formData.sort_order}
                  onChange={handleInputChange}
                  placeholder="0"
                />
              </div>
            </div>
          </div>

          {/* Features List */}
          <div className="mt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Features List</h3>
            {formData.features_list.map((feature, index) => (
              <div key={index} className="flex items-center gap-2 mb-2">
                <Input
                  value={feature}
                  onChange={(e) => handleFeatureChange(index, e.target.value)}
                  placeholder="Enter feature description"
                  className="flex-1"
                />
                {formData.features_list.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeFeature(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
            {errors.features_list && (
              <p className="text-red-500 text-sm mt-1">{errors.features_list}</p>
            )}
            <Button
              type="button"
              onClick={addFeature}
              variant="outline"
              className="mt-2"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Feature
            </Button>
          </div>

          {/* Features */}
          <div className="mt-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Feature Flags</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { key: 'feature_analytics', label: 'Analytics' },
                { key: 'feature_custom_branding', label: 'Custom Branding' },
                { key: 'feature_priority_support', label: 'Priority Support' },
                // { key: 'feature_api_access', label: 'API Access' },
                { key: 'feature_advanced_booking', label: 'Advanced Booking' },
                { key: 'feature_multi_location', label: 'Multi Location' }
              ].map(feature => (
                <label key={feature.key} className="flex items-center">
                  <input
                    type="checkbox"
                    name={feature.key}
                    checked={formData[feature.key]}
                    onChange={handleInputChange}
                    className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                  />
                  <span className="ml-2 text-sm text-gray-700">{feature.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Status */}
          <div className="mt-6">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleInputChange}
                className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
              />
              <span className="ml-2 text-sm text-gray-700">Active</span>
            </label>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading || isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading || isSubmitting}
              className="relative"
            >
              {isLoading || isSubmitting ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {isEdit ? 'Updating...' : 'Creating...'}
                </div>
              ) : (
                isEdit ? 'Update Subscription' : 'Create Subscription'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default CreateSubscriptionModal;
